import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { translate } from 'components/translation/translation';
import { Lens } from 'icone';
import { AssetType, getAssetsOverviewByProjectId } from 'lib/api_ts/assetsApi';
import { columnCentered, columnToRight } from 'lib/helpers/agGridHelpers';
import CustomTooltip from 'components/ag-grid/CustomTooltip';
import { formatToDecimals } from 'lib/helpers/formatting';
import React, { useEffect, useRef, useState } from 'react';
import { ButtonToolbar, Col, FormControl, InputGroup, Row } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { Loading } from '@components/atomic/atoms';

interface AssetsProps {
	projectId: number;
}

const Assets: React.FC<AssetsProps> = ({ projectId }) => {
	const [assets, setAssets] = useState<AssetType[]>([]);

	const { data: assetsFromApi, isLoading: isLoadingAssetsFromApi } = useQuery({
		queryKey: ['assetsOverview', projectId],
		queryFn: () => getAssetsOverviewByProjectId(projectId.toString()),
	});
	useEffect(() => {
		if (assetsFromApi) {
			setAssets(assetsFromApi);
		}
		// (async () => {
		// 	const assets = await getAssetsOverviewByProjectId(projectId.toString());
		// 	setAssets(assets);
		// })();
	}, [assetsFromApi]);

	const defaultColDef = {
		resizable: false,
		wrapText: true,
		autoHeight: true,
		flex: 1,
		suppressMovable: true,
		cellStyle: { padding: '0 1rem' },
		tooltipComponent: CustomTooltip,
	};

	const columnDefs = [
		{
			headerName: 'Voce',
			field: 'expenseVoice',
			tooltipField: 'expenseVoice',
			filter: 'agTextColumnFilter',
			minWidth: 160,
		},
		{
			headerName: 'Costo storico €',
			field: 'historicalCost',
			filter: 'agTextColumnFilter',
			filterValueGetter: (params: { data: { historicalCost: number } }) => {
				return formatToDecimals(params.data.historicalCost);
			},
			valueGetter: (params: { data: { historicalCost: number } }) => {
				return formatToDecimals(params.data.historicalCost);
			},
			minWidth: 190,
			...columnToRight(),
		},
		{
			headerName: 'Costo mensile €',
			field: 'monthlyCost',
			filter: 'agTextColumnFilter',
			filterValueGetter: (params: { data: { monthlyCost: number } }) => {
				return formatToDecimals(params.data.monthlyCost);
			},
			valueGetter: (params: { data: { monthlyCost: number } }) => {
				return formatToDecimals(params.data.monthlyCost);
			},
			minWidth: 180,
			...columnToRight(),
		},
		{
			headerName: 'Tempo utilizzo totale (in mesi)',
			headerClass: 'text-center',
			field: 'usageTimeMonths',
			filter: 'agTextColumnFilter',
			minWidth: 200,
			maxWidth: 200,
			headerComponent: () => {
				return (
					<div className="ag-cell-label-container">
						Tempo utilizzo totale
						<br />
						(in mesi)
					</div>
				);
			},
			...columnCentered(),
		},
		{
			headerName: 'Utilizzo sul progetto %',
			field: 'usagePercentage',
			filter: 'agTextColumnFilter',
			minWidth: 230,
			...columnToRight(),
		},
		{
			headerName: 'Mesi in rendicontazione (SAL attuale)',
			headerClass: 'text-center',
			field: 'statementMonths',
			filter: 'agTextColumnFilter',
			minWidth: 230,
			maxWidth: 230,
			headerComponent: () => {
				return (
					<div className="ag-cell-label-container">
						Mesi in rendicontazione
						<br />
						(SAL attuale)
					</div>
				);
			},
			...columnCentered(),
		},
		{
			headerName: 'Costo in rendicontazione €',
			field: 'statementAmount',
			filter: 'agTextColumnFilter',
			minWidth: 260,
			filterValueGetter: (params: { data: { statementAmount: number } }) => {
				return formatToDecimals(params.data.statementAmount);
			},
			valueGetter: (params: { data: { statementAmount: number } }) => {
				return formatToDecimals(params.data.statementAmount);
			},
			...columnToRight(),
		},
		{
			headerName: 'Mesi residui',
			field: 'remainingMonths',
			filter: 'agTextColumnFilter',
			minWidth: 150,
			maxWidth: 150,
			...columnCentered(),
		},
		{
			headerName: 'Costo residuo/rendicontabile €',
			field: 'remainingAmount',
			filter: 'agTextColumnFilter',
			minWidth: 300,
			filterValueGetter: (params: { data: { remainingAmount: number } }) => {
				return formatToDecimals(params.data.remainingAmount);
			},
			valueGetter: (params: { data: { remainingAmount: number } }) => {
				return formatToDecimals(params.data.remainingAmount);
			},
			...columnToRight(),
		},
	];

	//   const mockRowData = [
	//     {
	//       expenseVoice: "Voce di spesa 1",
	//       historicalCost: 1000,
	//       monthlyCost: 100,
	//       usageTimeMonths: 12,
	//       usagePercentage: 100,
	//       statementMonths: 6,
	//       statementAmount: 600,
	//       remainingMonths: 6,
	//       remainingAmount: 400,
	//     },
	//     {
	//       expenseVoice: "Voce di spesa 2",
	//       historicalCost: 2000,
	//       monthlyCost: 200,
	//       usageTimeMonths: 12,
	//       usagePercentage: 100,
	//       statementMonths: 6,
	//       statementAmount: 1200,
	//       remainingMonths: 6,
	//       remainingAmount: 800,
	//     },
	//   ];

	const formattedRowData = assets.map((asset) => ({
		expenseVoice: asset.name,
		historicalCost: asset.historicalCost,
		monthlyCost: asset.monthlyCost,
		usageTimeMonths: asset.usageTimeMonths,
		usagePercentage: asset.usagePercentage,
		statementMonths: asset.statementMonths,
		statementAmount: asset.statementAmount,
		remainingMonths: asset.remainingMonths,
		remainingAmount: asset.remainingAmount,
	}));

	const gridRef = useRef(null);

	return isLoadingAssetsFromApi ? (
		<Loading fixedHeight="50vh" />
	) : assets.length > 0 ? (
		//   return true ? (
		<>
			<ButtonToolbar className="justify-content-end mb-3">
				<InputGroup className="search-table align-items-center mt-3 mt-md-0">
					<FormControl
						type="text"
						className="filter-text-box"
						placeholder={translate('common.search', 'Cerca') + '...'}
						onInput={(event) => {
							gridRef.current.api.setQuickFilter('value' in event.target ? event.target.value : '');
						}}
					/>
					<Lens />
				</InputGroup>
			</ButtonToolbar>
			<div className="ag-theme-alpine list-table">
				<AgGridReact
					ref={gridRef}
					columnDefs={columnDefs}
					//  rowData={groups}
					defaultColDef={defaultColDef}
					rowData={formattedRowData}
					domLayout="autoHeight"
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					tooltipMouseTrack={true}
					//  onGridReady={onGridReady}
					//onFirstDataRendered={onFirstDataRendered}
					//onModelUpdated={onModelUpdated}
					headerHeight={50}
					rowHeight={50}
					//  localeText={localeText}
					//  ariaDescription="ag-grid-table"
					suppressCellFocus={true}
				/>
			</div>
		</>
	) : (
		<Row>
			<Col className="d-flex justify-content-center align-items-center">Nessun elemento disponibile</Col>
		</Row>
	);
};

export default Assets;
